<template>
  <div>
    <search-section />
    <div class="container">
      <div class="row g-4 mt-0">
        <div class="col-lg-3 d-none d-lg-block">
          <!-- Filters -->
          <div class="filter-section">
            <div class="card">
              <div class="card-body">
                <h2 class="filter-title mb-4">Categories</h2>

                <div class="filter-options">
                  <div
                    class="category-link"
                    v-for="(category, index) in categories"
                    :key="`category-${index}`"
                  >
                    <router-link
                      :to="'/category/?c=' + category.slug"
                      style="text-decoration: none"
                    >
                      <span class="category-text">
                        <i :class="category.icon" class="category-icon"></i>
                        {{ category.name }}
                      </span>
                    </router-link>
                  </div>
                </div>
              </div>
            </div>
            <div class="card">
              <div class="card-body">
                <h2 class="filter-title mb-4">Authors</h2>

                <div class="filter-options">
                  <div
                    class="category-link"
                    v-for="(author, i) in authors"
                    :key="`author-${i}`"
                  >
                    <router-link
                      :to="'/search/?a=' + author.replace(/ /g, '_')"
                      style="text-decoration: none"
                    >
                      <span class="category-text">{{ author }}</span>
                    </router-link>
                  </div>
                </div>
              </div>
            </div>

            <div class="card">
              <div class="card-body">
                <h2 class="filter-title mb-4">Featured Books</h2>
                <router-link
                  v-for="(book, i) in topBooks"
                  :key="`top-books-${i}`"
                  :to="'/book/' + book._id"
                  class="top-book-item"
                >
                  <img
                    :src="
                      book.images?.[0] ?
                      `${BASE_IMAGE_PREFIX}/${book.images[0]}` :
                      'https://libreture.com/static/images/book-placeholder.png'
                    "
                    :alt="book.title"
                    class="top-book-cover"
                  />
                  <div class="top-book-info">
                    <div class="top-book-title">{{ book.title }}</div>
                    <div
                      v-if="book.productCategory !== 'stationary'"
                      class="top-book-author"
                      >
                        {{ book.author }}
                      </div>
                    <div class="top-book-price" v-if="book.minVariant">
                      <span v-if="book.minVariant.salePrice">
                        <span class="text-decoration-line-through text-muted me-1">
                          ৳ {{ book.minVariant.price }}
                        </span>
                        <span>৳ {{ book.minVariant.salePrice }}</span>
                      </span>
                      <span v-else>৳ {{ book.minVariant.price }}</span>
                    </div>
                  </div>
                </router-link>
              </div>
            </div>
          </div>
        </div>
        <div class="col-lg-9">
          <div v-if="isLoading" class="text-center m-5">
            <div class="spinner-border mb-2" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p>Loading amazing books...</p>
          </div>
          <!-- Book Section -->
          <div v-else class="book-section">
            <div v-if="bookList.length" class="row g-4">
              <!-- Book Card -->
              <div
                v-for="(book, i) in bookList"
                :key="`book-${i}`"
                class="col-6 col-md-3"
              >
                <div class="book-card">
                  <router-link
                    :to="'/book/' + book._id + `/${slugify(book.title)}`"
                    style="text-decoration: none"
                  >
                    <img
                      :src="
                        book.images?.length
                          ? `${BASE_IMAGE_PREFIX}/${book.images[0]}`
                          : 'https://libreture.com/static/images/book-placeholder.png'
                      "
                      alt="Book Cover"
                      class="book-image"
                    />
                    <h5 class="book-title">{{ book.title }}</h5>
                  </router-link>
                  <div
                    v-if="book.productCategory !== 'stationary'"
                    class="book-author"
                  >
                    {{ book.author }}
                  </div>
                  <div class="book-price" v-if="book.minVariant">
                    <span v-if="book.minVariant.salePrice">
                      <span class="text-decoration-line-through text-muted me-1">
                        ৳ {{ book.minVariant.price }}
                      </span>
                      <span>৳ {{ book.minVariant.salePrice }}</span>
                    </span>
                    <span v-else>৳ {{ book.minVariant.price }}</span>
                  </div>
                  <!-- Product actions-->
                  <div
                    class="card-footer p-0 pb-4 pt-0 border-top-0 bg-transparent"
                  >
                    <div class="text-center">
                      <span
                        class="btn btn-outline-dark btn-sm mt-auto me-1 text-uppercase"
                        style="font-size: 10px"
                        @click="addToCart(book)"
                      >
                        <i class="bi bi-cart3"></i> Add to cart
                      </span>
                      <span
                        class="btn btn-outline-dark btn-sm mt-auto text-uppercase"
                        style="font-size: 10px"
                        @click="addToWishlist(book)"
                      >
                        <i class="bi bi-suit-heart"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="text-center mt-5">
              <p>No books available.</p>
            </div>
          </div>
          <!-- Pagination -->
          <div
            v-if="totalPages > 1"
            class="d-flex flex-wrap justify-content-center justify-content-md-end align-items-center my-4"
          >
            <!-- <p>Total Books: {{ totalBooks }}</p> -->
            <nav class="overflow-auto">
              <ul class="pagination mb-0 d-flex align-items-center">
                <li
                  class="page-item me-1"
                  :class="{ disabled: currentPage === 1 }"
                >
                  <button
                    class="page-link page-count"
                    @click="changePage(currentPage - 1)"
                  >
                    <i class="bi bi-arrow-left"></i>
                  </button>
                </li>
                <li
                  class="page-item mx-1"
                  v-for="(page, index) in visiblePages"
                  :key="index"
                >
                  <button
                    v-if="page !== '...'"
                    class="page-link page-count"
                    :class="{ active: page === currentPage }"
                    @click="changePage(page)"
                  >
                    {{ page }}
                  </button>
                  <span v-else class="page-link bg-transparent border-0 disabled fw-bolder">...</span>
                </li>
                <li
                  class="page-item me-1"
                  :class="{ disabled: currentPage === totalPages }"
                >
                  <button
                    class="page-link page-count"
                    @click="changePage(currentPage + 1)"
                  >
                    <i class="bi bi-arrow-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { BASE_URL, BASE_IMAGE_PREFIX } from "@/globals";
import SearchSection from "@/components/SearchSection.vue";
import { analytics } from '@/services/analytics';

export default {
  components: { SearchSection },
  name: "SearchPage",
  data() {
    return {
      BASE_IMAGE_PREFIX,
      searchQuery: null,
      categoryQuery: null,
      authorQuery: null,
      isLoading: false,
      searchTimeout: null,
      searchSuggestions: [],
      showSuggestions: true,
      bookList: [],
      categories: [
        {
          name: "Business",
          icon: "bi bi-briefcase",
          slug: "business",
        },
        {
          name: "Religion",
          icon: "bi bi-peace",
          slug: "religion",
        },
        {
          name: "Self Help",
          icon: "bi bi-emoji-smile",
          slug: "self_help",
        },
        {
          name: "Literature",
          icon: "bi bi-journal-bookmark",
          slug: "literature",
        },
        {
          name: "Science Fiction",
          icon: "bi bi-cpu",
          slug: "science_fiction",
        },
        {
          name: "Children & Teens",
          icon: "bi bi-people",
          slug: "children_and_teens",
        },
        {
          name: "Biographies & Memoirs",
          icon: "bi bi-person-lines-fill",
          slug: "biographies_and_memoirs",
        },
        {
          name: "Politics History and Tradition",
          icon: "bi bi-bank",
          slug: "politics_history_and_tradition",
        },
        {
          name: "Information and Technology",
          icon: "bi bi-motherboard",
          slug: "information_and_technology",
        },
        {
          name: "Stationery & Craft",
          icon: "bi bi-pencil-square",
          slug: "stationery_and_craft",
        },
      ],
      authors: [
        "Haruki Murakami",
        "Antoine De Saint Exupery",
        "Sun Tzu",
        "J. R. R. Tolkien",
        "Kazuo Ishiguro",
      ],
      currentPage: 1,
      totalPages: 1,
      totalBooks: 0,
      limit: 20,
    };
  },
  computed: {
    wishlistItemCount() {
      return this.$store.getters.wishlistItemCount;
    },
    topBooks() {
      return this.bookList.slice(0, 5);
    },
    visiblePages() {
      const total = this.totalPages;
      const current = this.currentPage;
      const delta = 1;

      let start = Math.max(1, current - delta);
      let end = Math.min(total, current + delta);
      let pages = [];

      if (start > 1) {
        pages.push(1);
        if (start > 2) pages.push("...");
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (end < total) {
        if (end < total - 1) pages.push("...");
        pages.push(total);
      }

      return pages;
    }
  },
  mounted() {
    this.searchQuery = this.$route.query.b || null;
    this.categoryQuery = this.$route.query.c || null;
    this.authorQuery = this.$route.query.a || null;
    const pageFromUrl = parseInt(this.$route.query.page, 10);
    this.currentPage = pageFromUrl > 0 ? pageFromUrl : 1;
    this.search();
    window.scrollTo(0, 0);
  },
  beforeRouteUpdate(to, from, next) {
    this.searchQuery = to.query.b || null;
    this.categoryQuery = to.query.c || null;
    this.authorQuery = to.query.a || null;

    const pageFromUrl = parseInt(to.query.page, 10);
    this.currentPage = pageFromUrl > 0 ? pageFromUrl : 1;

    this.search();
    next();
  },
  methods: {
    async search() {
      this.isLoading = true;
      try {
        // const response = await axios.get(`${BASE_URL}/api/products`, {
        //   params: {
        //     q: this.searchQuery?.trim() || undefined,
        //     category: this.categoryQuery ? this.categoryQuery : undefined,
        //     author: this.authorQuery ? this.authorQuery : undefined,
        //   },
        // });

        // this.bookList = response.data.books;
        // this.searchSuggestions = [];

        const response = await axios.get(`${BASE_URL}/api/products`, {
          params: {
            q: this.searchQuery?.trim() || undefined,
            category: this.categoryQuery || undefined,
            author: this.authorQuery || undefined,
            page: this.currentPage,
            limit: this.limit,
          },
        });

        this.bookList = response.data.books;
        this.totalPages = response.data.totalPages;
        this.totalBooks = response.data.totalBooks;
        this.searchSuggestions = [];
      } catch (error) {
        console.error("Error fetching books:", error);
      } finally {
        this.isLoading = false;
      }
    },
    async fetchSuggestions() {
      this.showSuggestions = true;
      try {
        const response = await axios.get(`${BASE_URL}/api/products`, {
          params: {
            q: this.searchQuery?.trim() || undefined,
            category: this.categoryQuery ? this.categoryQuery : undefined,
            author: this.authorQuery ? this.authorQuery : undefined,
          },
        });

        this.searchSuggestions = response.data.books;
      } catch (error) {
        console.error("Error fetching books:", error);
      } finally {
        this.isLoading = false;
      }
    },
    updateSearchQuery() {
      this.$router.push({
        path: "/search",
        query: this.searchQuery?.trim() ? { b: this.searchQuery } : {},
      });
    },
    selectResult(book) {
      this.$router.push(`/book/${book._id}`);
    },
    debounceSearch() {
      clearTimeout(this.searchTimeout);
      if (this.searchQuery.trim()) {
        this.searchTimeout = setTimeout(() => {
          this.fetchSuggestions();
        }, 500);
      }
    },
    addToCart(book) {
      const cartItem = {
        product: book,
        quantity: 1,
        variant: book.minVariant
      };

      this.$store.dispatch("addToCart", cartItem);

      this.$root.showToast({
        message: "Item added to cart",
        status: "success",
        duration: 3000,
      });
      analytics.addToCart(book,
        book.minVariant.salePrice || book.minVariant.price, 1)
    },
    addToWishlist(book) {
      const wishedItem = {
        ...book,
        quantity: 1,
        variant: book.minVariant
      };
      this.$store.dispatch("addToWishlist", wishedItem);
      this.$root.showToast({
        message: "Item added to wishlist",
        status: "success",
        duration: 3000,
      });
      analytics.addToWishlist(book,
        book.minVariant.salePrice || book.minVariant.price, 1)
    },
    changePage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.currentPage = page;
      this.$router.replace({
        query: {
          ...this.$route.query,
          page: page
        }
      });
      this.search();
    },
    slugify(text) {
      return text
        .toString()                  // convert to string
        .normalize("NFD")            // normalize accents (café → cafe)
        .replace(/[\u0300-\u036f]/g, "") // remove diacritics
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, "") // remove invalid chars
        .replace(/\s+/g, "-")         // collapse whitespace and replace by -
        .replace(/-+/g, "-");         // collapse dashes
    },
  },
};
</script>

<style lang="scss" scoped>
/* ################## */

/* Search Section */

.search-section {
  padding: 15px 0;
  .search-box {
    position: relative;
    .search-icon {
      position: absolute;
      top: 15px;
      right: 30px;
      font-size: 20px;
      cursor: pointer;
    }
    .search-icon:hover {
      color: var(--primary-color);
    }
  }
}

.categories-toggle {
  color: var(--light-pink);
  background-color: var(--primary-color);
  border: none;
  padding: 10px 50px;
  font-size: 20px;
  font-weight: 300;
  display: flex;
  align-items: center;
  gap: 8px;
  border-radius: 50px;
}

.search-input {
  border-radius: 30px;
  padding: 12px 25px;
  border: none;
  width: 100%;
  background: #f1f0f0;
}

.search-result-wrapper {
  position: relative;
  .search-dropdown {
    position: absolute;
    width: 100%;
    background: white;
    border: 1px solid #ddd;
    border-radius: 10px;
    max-height: 200px;
    overflow-y: auto;
    list-style: none;
    padding: 0;
    margin: 5px 0 0 0;
    z-index: 5;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }
  .search-dropdown li {
    padding: 10px;
    cursor: pointer;
    display: flex;
    align-items: center;
    .suggestion-book-image {
      width: auto;
      height: 40px;
      border-radius: 5px;
      margin-right: 10px;
      object-fit: cover;
    }
  }

  .search-dropdown li:hover {
    background: #f0f0f0;
  }
}

.store-locator {
  color: white;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
}

@media (max-width: 767px) {
  .categories-toggle {
    display: none;
  }
}

.category-text {
  font-size: 15px;
  font-weight: 400;
  color: #333;
}

.category-text:hover {
  opacity: 0.8;
  color: var(--primary-color);
}

.category-icon {
  margin-right: 5px;
  opacity: 0.5;
  font-size: 18px;
}

.category-link:hover .category-icon {
  opacity: 1;
}

/* Book Grid */
.book-tabs {
  border-bottom: none;
  margin-bottom: 30px;
}

.book-tabs .nav-link {
  color: var(--text-gray);
  border: none;
  padding: 10px 20px;
  font-weight: 500;
}

.book-tabs .nav-link.active {
  color: var(--primary-color);
  background: none;
  position: relative;
}

.book-tabs .nav-link.active::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 20px;
  right: 20px;
  height: 2px;
  background-color: var(--primary-color);
}

.book-card {
  background: white;
  border-radius: 8px;
  padding: 0 20px 0 0;
  text-align: center;
  transition: transform 0.3s;
  position: relative;
  /* box-shadow: 0 2px 10px rgba(0,0,0,0.05); */
}

.book-card::after {
  content: "";
  position: absolute;
  top: 0;
  right: 0;
  width: 1px;
  height: 100%;
  background-color: #ccc;
  opacity: 0.3;
}

.book-card:hover {
  transform: translateY(-5px);
}

.book-image {
  width: 100%;
  height: auto;
  border-radius: 15px;
  margin-bottom: 15px;
}

.book-title {
  font-weight: 600;
  margin-bottom: 8px;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.book-author {
  color: var(--text-gray);
  font-size: 14px;
  margin-bottom: 8px;
}

.book-rating {
  color: #ffc107;
  margin-bottom: 8px;
}

.book-price {
  color: var(--primary-color);
  font-weight: 700;
  font-size: 18px;
}

// Filters
.filter-section {
  .card {
    border-radius: 16px;
    border: 1px solid rgba(0, 0, 0, 0.1);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    margin-bottom: 15px;
    .top-book-item {
      display: flex;
      align-items: center;
      gap: 16px;
      margin-bottom: 20px;
      text-decoration: none;
      color: inherit;
      transition: transform 0.2s;
      position: relative;
    }

    .top-book-item:hover {
      transform: translateX(5px);
    }

    .top-book-cover {
      width: 80px;
      height: 120px;
      object-fit: cover;
      border-radius: 8px;
      flex-shrink: 0;
    }

    .top-book-info {
      flex-grow: 1;
    }

    .top-book-title {
      font-weight: 600;
      font-size: 16px;
      margin-bottom: 4px;
      color: #333;
    }

    .top-book-author,
    .top-book-price {
      font-size: 14px;
      color: #666;
    }
  }

  .filter-title {
    font-size: 24px;
    font-weight: 600;
    color: #000;
  }

  .filter-options {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .custom-checkbox {
    margin: 0;

    .form-check-input {
      width: 20px;
      height: 20px;
      margin-right: 12px;
      border: 2px solid #ddd;
      border-radius: 4px;
      cursor: pointer;

      &:checked {
        background-color: #0d6efd;
        border-color: #0d6efd;
      }
    }

    .form-check-label {
      font-size: 16px;
      color: #333;
      cursor: pointer;
      user-select: none;
    }
  }
}

.page-count {
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  font-size: 14px;
  font-weight: 500;
  border: none;
  background-color: #f8f9fa;
  color: #333;
  transition: all 0.2s ease;

  &:hover {
    background-color: #e2e6ea;
    cursor: pointer;
  }

  &.active,
  &:focus {
    background-color: var(--primary-color);
    color: white;
  }
}

@media (max-width: 991px) {
  .page-count {
    width: 30px;
    height: 30px;
    font-size: 10px;
  }
}
</style>

